import { useState } from 'react';
import { Bot } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAnswerAgentItem, type AnswerVars } from '@/queries/agent';
import { extractErrorMessage } from '@/queries/ticketMutations';
import { formatTime } from '@/lib/dates';
import { DENY_REASONS, itemState, type CardTone } from './agentLabels';
import type { ReactNode } from 'react';
import type { AgentItem } from '@/types/agent';
import { MessageMarkdown } from '@/components/message-markdown';

const TONE_CLASSES: Record<CardTone, string> = {
  pending: 'text-etn-info-text',
  waiting: 'text-etn-warning-text',
  done: 'text-etn-success-text',
  denied: 'text-etn-danger-text',
  closed: 'text-etn-text-soft'
};

function useAnswer(ticketId: string) {
  const mutation = useAnswerAgentItem(ticketId);
  const send = (vars: AnswerVars, onDone?: () => void) =>
    mutation.mutate(vars, {
      onSuccess: () => onDone?.(),
      // A 409 names who answered first and where ("Already answered by Ben on Slack"); the card refetches to show it.
      onError: err => toast.error(extractErrorMessage(err, 'The answer did not reach the agent'))
    });
  return { send, pending: mutation.isPending };
}

// Staff-side, never part of the customer's conversation: a dashed card on the staff side of the thread.
function CardShell({ item, title, all, children }: { item: AgentItem; title: string; all: AgentItem[]; children?: ReactNode }) {
  const state = itemState(item, all);
  const open = item.status === 'pending';
  return (
    <div className="mb-3.5 ml-auto flex max-w-[88%] flex-row-reverse gap-2.5" data-testid={`agent-${item.kind}-card`} data-status={item.status}>
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-etn-info-subtle text-etn-info-text" aria-hidden>
        <Bot className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`rounded-etn-card border border-dashed border-etn-border-input bg-etn-surface px-3.5 py-2.5 ${open ? '' : 'opacity-80'}`}>
          <div className="mb-1 flex flex-wrap items-baseline gap-2 text-etn-micro text-etn-text-soft">
            <span className="text-etn-control font-semibold text-etn-text-cell">{title}</span>
            <span className="font-semibold text-etn-warning-text">Staff only</span>
            <span>{formatTime(item.createdAt)}</span>
          </div>
          <MessageMarkdown>{item.text}</MessageMarkdown>
          {item.kind === 'draft' && item.disclosure && (
            <p className="mt-2 border-t border-border pt-2 text-xs italic text-etn-text-soft" data-testid="agent-item-disclosure">
              <span className="not-italic font-semibold">Added after your text: </span>
              {item.disclosure}
            </p>
          )}
          <p className={`mt-2 text-etn-micro font-semibold ${TONE_CLASSES[state.tone]}`} data-testid="agent-item-state">
            {state.label}
          </p>
          {state.detail && (
            <p className="mt-0.5 whitespace-pre-wrap text-xs text-etn-text-soft" data-testid="agent-item-detail">
              {state.detail}
            </p>
          )}
          {open && children}
        </div>
      </div>
    </div>
  );
}

function ReplyToAgent({
  onSend,
  onCancel,
  pending,
  placeholder
}: {
  onSend: (text: string) => void;
  onCancel: () => void;
  pending: boolean;
  placeholder: string;
}) {
  const [text, setText] = useState('');
  return (
    <div className="mt-2.5 space-y-2">
      <Textarea aria-label="Reply to the agent" autoFocus rows={3} value={text} onChange={e => setText(e.target.value)} placeholder={placeholder} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={pending || !text.trim()} onClick={() => onSend(text.trim())}>
          Send to agent
        </Button>
      </div>
    </div>
  );
}

function DenyBox({ onDeny, onCancel, pending }: { onDeny: (reason: string) => void; onCancel: () => void; pending: boolean }) {
  const [preset, setPreset] = useState<string | null>(null);
  const [detail, setDetail] = useState('');
  const reason = [preset, detail.trim()].filter(Boolean).join(': ');
  return (
    <div className="mt-2.5 space-y-2" data-testid="deny-box">
      <p className="text-xs font-semibold text-etn-text-label">Why deny it? The agent learns from this.</p>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Deny reason">
        {DENY_REASONS.map(option => (
          <button
            key={option}
            type="button"
            aria-pressed={preset === option}
            onClick={() => setPreset(current => (current === option ? null : option))}
            className={`rounded-etn-pill border px-2.5 py-0.5 text-etn-micro font-semibold ${
              preset === option
                ? 'border-etn-danger-text bg-etn-danger-subtle text-etn-danger-text'
                : 'border-etn-border-input text-etn-text-cell hover:bg-etn-control-subtle'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
      <Textarea
        aria-label="Deny reason details"
        rows={2}
        value={detail}
        onChange={e => setDetail(e.target.value)}
        placeholder="Add detail (optional with a reason above)"
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="destructive" disabled={pending || !reason} onClick={() => onDeny(reason)}>
          Deny draft
        </Button>
      </div>
    </div>
  );
}

type DraftMode = 'idle' | 'reply' | 'deny';

/**
 * An agent's draft reply waiting for staff (an Agent Relay approve question over its ticket_reply). An agent never sends
 * a customer reply itself: Send posts the draft as the person who pressed it, and Manual edit hands it to the composer,
 * whose Send posts their version. Only a send from here goes out; approvals elsewhere are refused.
 */
export function DraftCard({
  ticketId,
  item,
  all,
  onEdit
}: {
  ticketId: string;
  item: AgentItem;
  all: AgentItem[];
  onEdit: (item: AgentItem) => void;
}) {
  const [mode, setMode] = useState<DraftMode>('idle');
  const { send, pending } = useAnswer(ticketId);
  const answer = (vars: Omit<AnswerVars, 'questionId'>) => send({ questionId: item.questionId, ...vars }, () => setMode('idle'));
  // Someone pressed Send and it hasn't settled (Agent Relay's answer was lost): Send again retries their send, as they
  // sent it, which may be an edit of the draft above.
  const settling = item.sentBy && !item.sentBy.deliveredBy ? item.sentBy : undefined;
  // A delivery has claimed it and is posting it now: nothing to press until it settles.
  const posting = Boolean(item.sentBy?.deliveredBy);
  const sender = settling?.name ?? item.sentBy?.name ?? 'someone';

  return (
    <CardShell item={item} all={all} title={`Draft reply from ${item.agentName ?? 'the agent'}`}>
      {settling && (
        <div className="mt-2 rounded-etn-card bg-etn-warning-subtle px-3 py-2 text-xs text-etn-warning-text" data-testid="agent-item-settling">
          <p className="font-semibold">
            {sender} sent this, but it hasn’t been confirmed. Send again goes out as {sender} sent it.
          </p>
          {settling.text.trim() !== item.text.trim() && <p className="mt-1 whitespace-pre-wrap text-etn-text-cell">{settling.text}</p>}
        </div>
      )}
      {posting && (
        <p className="mt-2 text-xs font-semibold text-etn-info-text" data-testid="agent-item-posting">
          Being posted now, as {sender} sent it.
        </p>
      )}
      {mode === 'idle' && !posting && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={pending}
            onClick={() =>
              answer({
                action: 'approve',
                shown: item.text,
                shownDisclosure: item.disclosure ?? null,
                // The send the banner names: the server retries only that one.
                ...(settling && { retryOf: settling.at })
              })
            }
          >
            {settling ? `Send again as ${sender} sent it` : 'Send as me'}
          </Button>
          <Button size="sm" variant="outline" disabled={pending} onClick={() => onEdit(item)}>
            Manual edit
          </Button>
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setMode('reply')}>
            Reply to agent
          </Button>
          <Button size="sm" variant="destructive" disabled={pending} onClick={() => setMode('deny')}>
            Deny
          </Button>
        </div>
      )}
      {mode === 'reply' && (
        <ReplyToAgent
          pending={pending}
          placeholder="Tell the agent what to change. It investigates again and drafts a new reply."
          onCancel={() => setMode('idle')}
          onSend={text => answer({ action: 'guidance', text })}
        />
      )}
      {mode === 'deny' && <DenyBox pending={pending} onCancel={() => setMode('idle')} onDeny={reason => answer({ action: 'deny', reason })} />}
    </CardShell>
  );
}

type QuestionMode = 'idle' | 'reply' | 'ignore';

/** A question the agent asked staff because it needs more to go on. Ignore stands the agent down on this ticket. */
export function QuestionCard({ ticketId, item, all }: { ticketId: string; item: AgentItem; all: AgentItem[] }) {
  const [mode, setMode] = useState<QuestionMode>('idle');
  const { send, pending } = useAnswer(ticketId);
  const answer = (vars: Omit<AnswerVars, 'questionId'>) => send({ questionId: item.questionId, ...vars }, () => setMode('idle'));

  return (
    <CardShell item={item} all={all} title={`${item.agentName ?? 'The agent'} asks`}>
      {mode === 'idle' && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <Button size="sm" disabled={pending} onClick={() => setMode('reply')}>
            Reply to agent
          </Button>
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setMode('ignore')}>
            Ignore
          </Button>
        </div>
      )}
      {mode === 'reply' && (
        <ReplyToAgent
          pending={pending}
          placeholder="Answer the agent's question"
          onCancel={() => setMode('idle')}
          onSend={text => answer({ action: 'answer', text })}
        />
      )}
      {mode === 'ignore' && (
        <div className="mt-2.5 space-y-2" data-testid="ignore-confirm">
          <p className="text-xs text-etn-text-cell">
            The agent stands down and leaves this ticket alone, even when the customer replies, until someone brings it back.
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
              Cancel
            </Button>
            <Button size="sm" disabled={pending} onClick={() => answer({ action: 'ignore' })}>
              Ignore and stand the agent down
            </Button>
          </div>
        </div>
      )}
    </CardShell>
  );
}

export function AgentItemCard({
  ticketId,
  item,
  all,
  onEdit
}: {
  ticketId: string;
  item: AgentItem;
  all: AgentItem[];
  onEdit: (item: AgentItem) => void;
}) {
  return item.kind === 'draft' ? (
    <DraftCard ticketId={ticketId} item={item} all={all} onEdit={onEdit} />
  ) : (
    <QuestionCard ticketId={ticketId} item={item} all={all} />
  );
}
